import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, KeyRound, CheckCircle2, XCircle, ScrollText, Radar, Rss, RefreshCw, Stethoscope, Loader2, HardDriveUpload, RotateCcw, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/lib/constants";
import TechCatalogManager from "@/pages/blueprints/TechCatalogManager";
import TestDataCleanup from "@/pages/TestDataCleanup";
import PolicyTemplateLibrary from "@/pages/policy/PolicyTemplateLibrary";
import SSPTemplateLibrary from "@/pages/ssp/SSPTemplateLibrary";

const STATUS_STYLES = {
  ok: "bg-emerald-100 text-emerald-800",
  not_configured: "bg-slate-100 text-slate-600",
  auth_error: "bg-red-100 text-red-800",
};

export default function AdminSettings() {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  // SAM.gov
  const [sam, setSam] = useState(null);
  const [samKey, setSamKey] = useState("");
  const [samTest, setSamTest] = useState("");
  // Feed
  const [feed, setFeed] = useState(null);
  // Reconciliation upload settings
  const [reconCfg, setReconCfg] = useState(null);
  const [reconMe, setReconMe] = useState(null);
  const [docMb, setDocMb] = useState("50");
  const [evMb, setEvMb] = useState("50");
  const [savingCfg, setSavingCfg] = useState(false);

  const load = () => {
    api.get("/settings/apollo").then((r) => setStatus(r.data));
    api.get("/enrichment/logs").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/settings/samgov").then((r) => setSam(r.data)).catch(() => {});
    api.get("/feed/settings").then((r) => setFeed(r.data)).catch(() => {});
    api.get("/recon/me").then((r) => setReconMe(r.data)).catch(() => {});
    api.get("/recon/config").then((r) => { setReconCfg(r.data); setDocMb(String(r.data.max_upload_mb)); setEvMb(String(r.data.max_evidence_mb)); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const validCfg = (d, e) => {
    const dn = Number(d), en = Number(e);
    if (!Number.isInteger(dn) || !Number.isInteger(en)) return "Enter whole numbers only.";
    if (dn < 1 || dn > 500 || en < 1 || en > 500) return "Values must be between 1 and 500 MB.";
    return null;
  };
  const saveReconCfg = async () => {
    const err = validCfg(docMb, evMb);
    if (err) { toast.error(err); return; }
    setSavingCfg(true);
    try {
      const r = await api.put("/recon/config", { max_upload_mb: Number(docMb), max_evidence_mb: Number(evMb) });
      setReconCfg(r.data); toast.success("Reconciliation upload settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSavingCfg(false); }
  };
  const restoreReconDefaults = () => {
    const def = reconCfg?.defaults || { max_upload_mb: 50, max_evidence_mb: 50 };
    setDocMb(String(def.max_upload_mb)); setEvMb(String(def.max_evidence_mb));
    toast.info("Defaults restored — click Save to apply.");
  };

  const saveSam = async () => {
    try { await api.put("/settings/samgov", { api_key: samKey.trim() }); toast.success("SAM.gov key saved"); setSamKey(""); load(); }
    catch { toast.error("Failed to save"); }
  };
  const removeSam = async () => { await api.delete("/settings/samgov"); toast.success("SAM.gov key removed"); load(); };
  const testSam = async () => {
    setSamTest("testing");
    try { const r = await api.post("/settings/samgov/test"); setSamTest(`OK · ${r.data.total_records} records reachable`); toast.success("SAM.gov connection OK"); load(); }
    catch (e) { setSamTest(e?.response?.data?.detail || "Connection failed"); toast.error("SAM.gov test failed"); }
  };
  const saveFeed = async (patch) => {
    try { const r = await api.put("/feed/settings", patch); setFeed(r.data); toast.success("Feed settings updated"); }
    catch { toast.error("Failed"); }
  };
  const refreshFeed = async () => { try { await api.post("/feed/refresh"); toast.success("Feed refreshed"); load(); } catch { toast.error("Refresh failed"); } };

  const save = async () => {
    if (!apiKey.trim()) { toast.error("Enter an API key"); return; }
    setSaving(true);
    try {
      await api.put("/settings/apollo", { api_key: apiKey.trim() });
      toast.success("Apollo API key saved securely");
      setApiKey("");
      load();
    } catch {
      toast.error("Failed to save key");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await api.delete("/settings/apollo");
    toast.success("Apollo API key removed");
    load();
  };

  const runDiagnostics = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await api.post("/settings/apollo/diagnostics");
      setDiag(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostics failed");
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none flex items-center gap-2">
          <Settings className="h-6 w-6 text-gold" aria-hidden="true" /> Admin Settings
        </h1>
      </header>

      <div className="p-6 md:p-8 space-y-8">
        {/* Reconciliation Settings */}
        <section className="rounded-md border border-border bg-card" data-testid="recon-settings-section">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <HardDriveUpload className="h-4 w-4 text-gold-dark" aria-hidden="true" />
              <h2 className="font-display font-bold text-navy">Reconciliation Settings</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <ShieldCheck className="h-4 w-4" /> Super Admin only
            </span>
          </div>
          <div className="p-5 space-y-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              System-level upload limits for the Implementation Reconciliation module. These apply to all reconciliations and evidence uploads.
            </p>
            {reconMe && !reconMe?.caps?.manage_roles && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Only a Super Admin can change these values. You can view the current configuration below.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Maximum reconciliation document upload size (MB)</Label>
                <Input type="number" min={1} max={500} value={docMb} onChange={(e) => setDocMb(e.target.value)}
                  disabled={!reconMe?.caps?.manage_roles} data-testid="recon-cfg-doc-mb" />
                <p className="text-[11px] text-muted-foreground">Accepted: {(reconCfg?.doc_exts || ["pdf", "docx"]).map((x) => x.toUpperCase()).join(", ")}</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Maximum evidence upload size (MB)</Label>
                <Input type="number" min={1} max={500} value={evMb} onChange={(e) => setEvMb(e.target.value)}
                  disabled={!reconMe?.caps?.manage_roles} data-testid="recon-cfg-ev-mb" />
                <p className="text-[11px] text-muted-foreground">Accepted: {(reconCfg?.evidence_exts || []).map((x) => x.toUpperCase()).join(", ")}</p>
              </div>
            </div>
            {reconMe?.caps?.manage_roles && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button className="bg-gold text-navy hover:bg-gold-dark" onClick={saveReconCfg} disabled={savingCfg} data-testid="recon-cfg-save">
                  {savingCfg ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save
                </Button>
                <Button variant="outline" onClick={restoreReconDefaults} data-testid="recon-cfg-restore">
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Restore Default (50 MB)
                </Button>
                <span className="text-[11px] text-muted-foreground">All changes are recorded in the reconciliation audit log.</span>
              </div>
            )}
          </div>
        </section>

        {/* Technology Catalog */}
        <TechCatalogManager me={reconMe} />

        {/* Policy Template Library */}
        <PolicyTemplateLibrary me={reconMe} />

        {/* SSP Master Template Library */}
        <SSPTemplateLibrary me={reconMe} />

        {/* Test Data Cleanup */}
        <TestDataCleanup me={reconMe} />

        {/* Apollo integration */}
        <section className="rounded-md border border-border bg-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-gold-dark" aria-hidden="true" />
              <h2 className="font-display font-bold text-navy">Apollo.io Contact Enrichment</h2>
            </div>
            {status && (
              status.configured ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="apollo-connected">
                  <CheckCircle2 className="h-4 w-4" /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="apollo-disconnected">
                  <XCircle className="h-4 w-4" /> Not connected
                </span>
              )
            )}
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Powers the <span className="font-medium text-navy">Find Decision Maker</span> feature. Enter your Apollo API key to
              enable live people search and enrichment. The key is stored securely server-side and never exposed to Sales Users.
              Get a key from Apollo → Settings → Integrations → API.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label className="text-xs">Apollo API Key</Label>
                <Input type="password" data-testid="apollo-key-input" placeholder="••••••••••••••••"
                  value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
              </div>
              <Button onClick={save} disabled={saving} data-testid="apollo-save-button"
                className="bg-navy text-white hover:bg-navy-700">
                {saving ? "Saving…" : status?.configured ? "Update Key" : "Save Key"}
              </Button>
              {status?.configured && (
                <Button variant="outline" onClick={remove} data-testid="apollo-remove-button"
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700">
                  Remove
                </Button>
              )}
            </div>
            {status?.updated_at && (
              <p className="text-[11px] text-muted-foreground">Last updated {fmtDate(status.updated_at)}</p>
            )}

            {/* Apollo Diagnostics */}
            <div className="mt-2 rounded-md border border-border bg-slate-50/60 p-4" data-testid="apollo-diagnostics">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                  <h3 className="font-display font-bold text-navy text-sm">Apollo Diagnostics</h3>
                </div>
                <Button size="sm" variant="outline" onClick={runDiagnostics} disabled={diagLoading} data-testid="apollo-diagnostics-run">
                  {diagLoading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Running…</> : "Run Diagnostics"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Tests the current People API Search endpoint (<span className="font-mono">/api/v1/mixed_people/api_search</span>),
                Organization Enrichment, and People Match against a sample company. Verifies whether the key is accepted and the
                endpoint is authorized (a master API key is required for People Search).
              </p>

              {diag && (
                <div className="space-y-3" data-testid="apollo-diagnostics-result">
                  <div className="flex flex-wrap gap-2 text-[11px]">
                    <span className={`px-2 py-0.5 rounded border ${diag.key_present ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-red-100 text-red-800 border-red-200"}`}>
                      Key present: {diag.key_present ? "Yes" : "No"}
                    </span>
                    <span className={`px-2 py-0.5 rounded border ${diag.key_loaded_server_side ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      Loaded server-side: {diag.key_loaded_server_side ? "Yes" : "No"}
                    </span>
                    {diag.last_http_status != null && (
                      <span className="px-2 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-200">Last HTTP: {diag.last_http_status}</span>
                    )}
                  </div>

                  {(diag.steps || []).map((s, i) => (
                    <div key={i} className="rounded border border-border bg-white p-3" data-testid={`diagnostics-step-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {s.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
                          <span className="font-medium text-navy text-sm">{s.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px]">
                          {s.http_status != null && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">HTTP {s.http_status}</span>}
                          {s.category && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{s.category}</span>}
                          {s.duration_ms != null && <span className="text-muted-foreground">{s.duration_ms}ms</span>}
                        </div>
                      </div>
                      {s.endpoint && <p className="text-[11px] text-muted-foreground font-mono mt-1">{s.endpoint}</p>}
                      <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                        {"key_accepted" in s && (
                          <span className={`px-1.5 py-0.5 rounded border ${s.key_accepted ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                            Key accepted: {s.key_accepted ? "Yes" : "No"}
                          </span>
                        )}
                        {"endpoint_authorized" in s && (
                          <span className={`px-1.5 py-0.5 rounded border ${s.endpoint_authorized ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            Endpoint authorized: {s.endpoint_authorized ? "Yes" : "No"}
                          </span>
                        )}
                        {s.result_count != null && <span className="px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200">Results: {s.result_count}</span>}
                      </div>
                      {s.detail && <p className="text-[11px] text-amber-700 mt-2">{s.detail}</p>}
                      {s.redacted_body && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-gold-dark cursor-pointer">Redacted response</summary>
                          <pre className="mt-1 text-[10px] bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-40">{s.redacted_body}</pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SAM.gov integration */}
        <section className="rounded-md border border-border bg-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Radar className="h-4 w-4 text-gold-dark" aria-hidden="true" />
              <h2 className="font-display font-bold text-navy">Opportunity Trigger Finder (SAM.gov)</h2>
            </div>
            {sam && (sam.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-testid="sam-connected"><CheckCircle2 className="h-4 w-4" /> Connected</span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="sam-disconnected"><XCircle className="h-4 w-4" /> Not connected</span>
            ))}
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Powers the <span className="font-medium text-navy">Opportunity Trigger Finder</span> tab. Get a SAM.gov API key from your
              SAM.gov account → Account Details. The key is stored server-side only and never exposed to Sales Users.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label className="text-xs">SAM.gov API Key</Label>
                <Input type="password" data-testid="sam-key-input" placeholder="••••••••••••••••" value={samKey} onChange={(e) => setSamKey(e.target.value)} />
              </div>
              <Button onClick={saveSam} data-testid="sam-save-button" className="bg-navy text-white hover:bg-navy-700">{sam?.configured ? "Update Key" : "Save Key"}</Button>
              {sam?.configured && <Button variant="outline" onClick={testSam} data-testid="sam-test-button">Test connection</Button>}
              {sam?.configured && <Button variant="outline" onClick={removeSam} className="text-red-600 border-red-200 hover:bg-red-50">Remove</Button>}
            </div>
            {samTest && <p className="text-xs text-muted-foreground" data-testid="sam-test-result">{samTest === "testing" ? "Testing…" : samTest}</p>}
            {sam?.last_call && <p className="text-[11px] text-muted-foreground">Last SAM.gov call: {fmtDate(sam.last_call)}</p>}
            {sam && (
              <div className="grid sm:grid-cols-3 gap-3 pt-2 text-xs">
                <div className="rounded border border-border p-3"><p className="text-muted-foreground mb-1">Default keywords</p><p className="text-navy leading-relaxed">{(sam.keywords || []).slice(0, 8).join(", ")}…</p></div>
                <div className="rounded border border-border p-3"><p className="text-muted-foreground mb-1">Default agencies</p><p className="text-navy leading-relaxed">{(sam.agencies || []).slice(0, 8).join(", ")}…</p></div>
                <div className="rounded border border-border p-3"><p className="text-muted-foreground mb-1">Fit threshold / cache</p><p className="text-navy">Fit ≥ {sam.fit_threshold} · cache {sam.cache_hours}h</p></div>
              </div>
            )}
          </div>
        </section>

        {/* Update Feed settings */}
        <section className="rounded-md border border-border bg-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Rss className="h-4 w-4 text-gold-dark" aria-hidden="true" />
              <h2 className="font-display font-bold text-navy">Update Feed Settings</h2>
            </div>
            <Button size="sm" variant="outline" onClick={refreshFeed} data-testid="feed-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh now</Button>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">Controls the Defense &amp; CMMC Updates feed on the login page. Cached server-side to keep login fast.</p>
            {feed && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch id="feed-enabled" data-testid="feed-enabled-toggle" checked={feed.enabled} onCheckedChange={(v) => saveFeed({ enabled: v })} />
                    <Label htmlFor="feed-enabled" className="text-sm cursor-pointer">Show feed on login page</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Cache (hours)</Label>
                    <Input type="number" className="w-20 h-9" defaultValue={feed.cache_hours}
                      onBlur={(e) => saveFeed({ cache_hours: parseInt(e.target.value) || 8 })} data-testid="feed-cache-hours" />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Sources: {(feed.sources || []).join(", ")}
                  {feed.last_success && <> · Last success: {fmtDate(feed.last_success)}</>}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Enrichment logs */}
        <section className="rounded-md border border-border bg-card">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <ScrollText className="h-4 w-4 text-gold-dark" aria-hidden="true" />
            <h2 className="font-display font-bold text-navy">Enrichment Usage Log</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-left">
                  {["When", "Company", "Identifier", "By", "Status", "Results"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold text-navy text-xs uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground text-sm">No enrichment attempts yet.</td></tr>
                ) : logs.map((l) => (
                  <tr key={l.id} className="border-b border-border last:border-0" data-testid="enrichment-log-row">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground text-xs">{fmtDate(l.searched_at)}</td>
                    <td className="px-4 py-3 font-medium text-navy">{l.company_name || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{l.identifier_used || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{l.searched_by}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_STYLES[l.status] || "bg-slate-100"}`}>{l.status}</span>
                    </td>
                    <td className="px-4 py-3">{l.result_count ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
